import React, { useState } from 'react';
import { CreditCardIcon, SettingsIcon, UsersRoundIcon } from 'lucide-react';
import { AccountPanel, AccountProfile } from '../components/AccountPanel';
import { BrandMark } from '../components/BrandMark';
import { PaymentsPanel } from '../components/PaymentsPanel';
import { TeamPanel } from '../components/TeamPanel';
import { UserDetails } from '../components/UserDetails';
import { UsersPanel } from '../components/UsersPanel';
import { Customer, seededPayments, seededUsers, UserStatus } from '../data/demoData';
import { useScreenInit } from '../useScreenInit';
type AdminView = 'users' | 'payments' | 'team' | 'account' | 'details';
const initialAccount: AccountProfile = {
  firstName: 'Ian',
  lastName: 'Carter',
  email: 'ian@creditmanager.co.uk'
};
export function AdminPortal() {
  const screenInit = useScreenInit() as {
    view?: AdminView;
    selectedUserId?: string;
  };
  const [view, setView] = useState<AdminView>(() => isAdminView(screenInit.view) ? screenInit.view : 'users');
  const [users, setUsers] = useState<Customer[]>(seededUsers);
  const [account, setAccount] = useState<AccountProfile>(initialAccount);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(() => screenInit.selectedUserId || (screenInit.view === 'details' ? seededUsers[0].id : null));
  const selectedUser = users.find(user => user.id === selectedUserId);
  const accountName = `${account.firstName} ${account.lastName}`;
  function updateStatus(id: string, status: UserStatus) {
    setUsers(current => current.map(user => user.id === id ? {
      ...user,
      status
    } : user));
  }
  function selectUser(user: Customer) {
    setSelectedUserId(user.id);
    setView('details');
  }
  const navItems = [{
    id: 'users' as const,
    label: 'Users',
    icon: UsersRoundIcon
  }, {
    id: 'payments' as const,
    label: 'Payments',
    icon: CreditCardIcon
  }, {
    id: 'team' as const,
    label: 'Team',
    icon: SettingsIcon
  }];
  return <div className="min-h-screen w-full bg-[#A6B6F8]/10 text-[#041F60]" data-id="element-529" data-mp="2jqqyj6spyvy5">
      <div className="mx-auto flex min-h-screen max-w-[1440px]" data-id="element-530" data-mp="2op26fbzgewdq">
        <aside className="navy-texture sticky top-0 hidden h-screen w-60 shrink-0 border-r border-[#031849] p-4 md:block" data-id="element-531" data-mp="2toet9wqbs3gb">
          <div className="px-2 pb-7 pt-1" data-id="element-532" data-mp="2yo18gbpprtrr">
            <BrandMark inverse data-id="element-533" data-mp="2yn9bawggxnzd" />
          </div>
          <nav aria-label="Admin navigation" className="space-y-1" data-id="element-534" data-mp="32ol398mtelaq">
            {navItems.map(item => {
            const Icon = item.icon;
            const isActive = view === item.id || view === 'details' && item.id === 'users';
            return <button key={item.id} type="button" onClick={() => setView(item.id)} className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-bold outline-none transition focus:ring-2 focus:ring-[#4BE4FF] focus:ring-offset-2 focus:ring-offset-[#041F60] ${isActive ? 'bg-[#4BE4FF] text-[#041F60]' : 'text-white/75 hover:bg-white/10 hover:text-white'}`} data-id="element-535" data-mp="3hdzfqplotiuc">
                  <Icon size={18} aria-hidden="true" data-id="element-536" data-mp="10tnbf38vc1wj" />
                  {item.label}
                </button>;
          })}
          </nav>

          <div className="absolute bottom-5 left-4 right-4 border-t border-white/15 pt-4" data-id="element-537" data-mp="1tghvhz1rwz9h">
            <button type="button" onClick={() => setView('account')} aria-label={`Open account settings for ${accountName}`} className={`flex w-full items-center gap-3 rounded-lg p-2 text-left outline-none transition focus:ring-2 focus:ring-[#4BE4FF] focus:ring-offset-2 focus:ring-offset-[#041F60] ${view === 'account' ? 'bg-white/10' : 'hover:bg-white/10'}`} data-id="element-538" data-mp="1oglrw0wzn9ug">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#4BE4FF] text-xs font-bold text-[#041F60]" aria-hidden="true" data-id="element-539" data-mp="27eg6qvkssfkr">
                {initialsFor(account)}
              </span>
              <span className="min-w-0" data-id="element-540" data-mp="1i0btzvnsml1x">
                <span className="block truncate text-sm font-bold text-white" data-id="element-541" data-mp="1i1d705oddhma">
                  {accountName}
                </span>
                <span className="block truncate text-xs text-[#A6B6F8]" data-id="element-542" data-mp="1n0q0vadnroyd">
                  {account.email}
                </span>
              </span>
            </button>
          </div>
        </aside>

        <main className="min-w-0 flex-1 px-5 py-7 sm:px-7 sm:py-9" data-id="element-543" data-mp="20yo4bkqfy0z3">
          <nav aria-label="Admin navigation" className="mb-6 flex gap-2 overflow-x-auto md:hidden" data-id="element-544" data-mp="20yebzqhxbhq8">
            {navItems.map(item => {
            const Icon = item.icon;
            const isActive = view === item.id || view === 'details' && item.id === 'users';
            return <button key={item.id} type="button" onClick={() => setView(item.id)} className={`inline-flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-bold ${isActive ? 'bg-[#041F60] text-white' : 'border border-[#A6B6F8]/60 bg-white text-[#3A3A3A]'}`} data-id="element-545" data-mp="myjlgsjywu9e">
                  <Icon size={16} aria-hidden="true" data-id="element-546" data-mp="rs5pxxfz9ru4" />
                  {item.label}
                </button>;
          })}
          </nav>

          {view === 'users' && <UsersPanel users={users} payments={seededPayments} onSelect={selectUser} onStatusChange={updateStatus} data-id="element-547" data-mp="3v8mlxy6wu8br" />}
          {view === 'payments' && <PaymentsPanel payments={seededPayments} users={users} data-id="element-548" data-mp="1ofj1gm6v5t8q" />}
          {view === 'team' && <TeamPanel data-id="element-549" data-mp="1kf064i5ri51s" />}
          {view === 'account' && <AccountPanel profile={account} onProfileSave={setAccount} data-id="element-550" data-mp="1kgjme8rf4g3k" />}
          {view === 'details' && selectedUser && <UserDetails user={selectedUser} payments={seededPayments.filter(payment => payment.userId === selectedUser.id)} onBack={() => setView('users')} onStatusChange={status => updateStatus(selectedUser.id, status)} data-id="element-551" data-mp="m24ofxv03kvr" />}
        </main>
      </div>
    </div>;
}
function isAdminView(value: unknown): value is AdminView {
  return value === 'users' || value === 'payments' || value === 'team' || value === 'account' || value === 'details';
}
function initialsFor(profile: AccountProfile) {
  return `${profile.firstName.charAt(0)}${profile.lastName.charAt(0)}`.toUpperCase();
}